// src/WorkspaceContext.js
//
// Pass 44 — workspace context. One human = one account; the *workspace* (Personal /
// Instructor / Organisation / Platform Admin) determines nav + home, NEVER identity.
// Available workspaces are derived from capabilities; the active one tracks the URL
// so any deep link resolves deterministically. Persists the last valid workspace.

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { WORKSPACES, availableWorkspaces, workspaceForPath } from './routes';
import {
  canEnterOrganizationWorkspace, canEnterInstructorWorkspace, canManageContentPack, canManageSubscriptionPlans,
  canPreviewStudentWorkspace,
} from './utils/platformAccess';

const WorkspaceContext = createContext(null);
const STORAGE_KEY = 'motus-workspace';

export function useWorkspace() {
  return useContext(WorkspaceContext) || {
    workspaces: [], currentWorkspace: 'personal', setWorkspace: () => {},
    canAccessWorkspace: () => false, defaultWorkspace: 'personal', WORKSPACES,
  };
}

export function WorkspaceProvider({ children }) {
  const { userData, currentUser } = useAuth() || {};
  const location = useLocation();
  const navigate = useNavigate();

  const workspaces = useMemo(() => {
    if (!currentUser || !userData) return [];
    const orgId = userData.orgId || userData.organizationId || '';
    // Pass 58C — workspace availability is the canonical ENTITLEMENT: Operations = operating permission
    // (owner/admin/ops); Instructor = a TEACHING relationship (isTeachingInstructor), not a membership by
    // itself and not the permission role. So a plain instructor does NOT get Operations, and an ops user
    // who does not teach (Serena) does NOT get Instructor.
    return availableWorkspaces(userData, {
      hasOrgAccess: canEnterOrganizationWorkspace(userData || {}, orgId),
      isInstructor: canEnterInstructorWorkspace(userData || {}, orgId),
      isPlatformAdmin: canManageContentPack(userData || {}) || canManageSubscriptionPlans(userData || {}),
      // Pass 82D — org owner/admin (or super-admin) also get the read-only Student Preview workspace.
      canPreviewStudent: canPreviewStudentWorkspace(userData || {}, orgId),
    });
  }, [userData, currentUser]);

  // Default landing workspace: the "highest" operational one available.
  const defaultWorkspace = useMemo(() => {
    for (const k of ['organization', 'instructor', 'personal']) if (workspaces.includes(k)) return k;
    if (workspaces.includes('admin')) return 'admin';
    return 'personal';
  }, [workspaces]);

  const [stored, setStored] = useState(() => {
    try { return window.localStorage.getItem(STORAGE_KEY) || ''; } catch { return ''; }
  });

  // Active workspace: the route wins (deterministic from URL); otherwise the stored
  // preference; otherwise the default. Only ever a workspace the user may access.
  const currentWorkspace = useMemo(() => {
    const routeWs = workspaceForPath(location.pathname);
    if (routeWs && workspaces.includes(routeWs)) return routeWs;
    if (stored && workspaces.includes(stored)) return stored;
    return defaultWorkspace;
  }, [location.pathname, workspaces, stored, defaultWorkspace]);

  // Persist whenever the route establishes a valid workspace.
  useEffect(() => {
    const routeWs = workspaceForPath(location.pathname);
    if (routeWs && workspaces.includes(routeWs) && routeWs !== stored) {
      try { window.localStorage.setItem(STORAGE_KEY, routeWs); } catch { /* ignore */ }
      setStored(routeWs);
    }
  }, [location.pathname, workspaces, stored]);

  const canAccessWorkspace = (key) => workspaces.includes(key);

  const setWorkspace = (key) => {
    if (!workspaces.includes(key)) return;
    try { window.localStorage.setItem(STORAGE_KEY, key); } catch { /* ignore */ }
    setStored(key);
    navigate(WORKSPACES[key].home);
  };

  const value = { workspaces, currentWorkspace, setWorkspace, canAccessWorkspace, defaultWorkspace, WORKSPACES };
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}
